import axios, { AxiosError } from 'axios';

import { HTTP_STATUS, isRetryableStatus } from './status';

export type ApiErrorCode =
    | 'BAD_REQUEST'
    | 'CANCELLED'
    | 'CONFLICT'
    | 'FORBIDDEN'
    | 'NETWORK_ERROR'
    | 'NOT_FOUND'
    | 'RATE_LIMITED'
    | 'SERVER_ERROR'
    | 'TIMEOUT'
    | 'UNAUTHORIZED'
    | 'UNKNOWN'
    | 'VALIDATION_ERROR';

type ApiErrorPayload = {
    code?: string;
    error?: unknown;
    errors?: unknown;
    message?: unknown;
};

export class ApiServiceError extends Error {
    code: ApiErrorCode;
    data?: unknown;
    details?: unknown;
    isRetryable: boolean;
    requestId?: string;
    status?: number;

    constructor(message: string, options: Partial<ApiServiceError> = {}) {
        super(message);
        this.name = 'ApiServiceError';
        this.code = options.code ?? 'UNKNOWN';
        this.status = options.status;
        this.data = options.data;
        this.details = options.details;
        this.requestId = options.requestId;
        this.isRetryable = options.isRetryable ?? false;
    }
}

export function isApiServiceError(error: unknown): error is ApiServiceError {
    return error instanceof ApiServiceError;
}

function coercePayload(data: unknown): ApiErrorPayload {
    return data && typeof data === 'object' ? (data as ApiErrorPayload) : {};
}

function extractMessage(data: unknown, fallbackMessage: string): string {
    if (typeof data === 'string' && data.trim().length > 0) {
        return data;
    }

    const payload = coercePayload(data);

    if (typeof payload.message === 'string' && payload.message.trim().length > 0) {
        return payload.message;
    }

    if (typeof payload.error === 'string' && payload.error.trim().length > 0) {
        return payload.error;
    }

    return fallbackMessage;
}

function codeFromStatus(status?: number, fallback: ApiErrorCode = 'UNKNOWN'): ApiErrorCode {
    switch (status) {
        case HTTP_STATUS.BAD_REQUEST:
            return 'BAD_REQUEST';
        case HTTP_STATUS.UNAUTHORIZED:
            return 'UNAUTHORIZED';
        case HTTP_STATUS.FORBIDDEN:
            return 'FORBIDDEN';
        case HTTP_STATUS.NOT_FOUND:
            return 'NOT_FOUND';
        case HTTP_STATUS.CONFLICT:
            return 'CONFLICT';
        case HTTP_STATUS.UNPROCESSABLE_ENTITY:
            return 'VALIDATION_ERROR';
        case HTTP_STATUS.TOO_MANY_REQUESTS:
            return 'RATE_LIMITED';
        default:
            return status && status >= 500 ? 'SERVER_ERROR' : fallback;
    }
}

function requestIdFrom(error: AxiosError): string | undefined {
    const headers = error.response?.headers;

    if (!headers) {return undefined;}

    const requestId = headers['x-request-id'] ?? headers['x-correlation-id'];

    return Array.isArray(requestId) ? requestId[0] : requestId;
}

export function toApiServiceError(error: unknown, fallbackMessage = 'Request failed'): ApiServiceError {
    if (isApiServiceError(error)) {
        return error;
    }

    if (axios.isCancel(error)) {
        return new ApiServiceError('Request canceled', {
            code: 'CANCELLED',
            isRetryable: false,
        });
    }

    if (!axios.isAxiosError(error)) {
        const message = error instanceof Error ? error.message : fallbackMessage;

        return new ApiServiceError(message, { code: 'UNKNOWN' });
    }

    const status = error.response?.status;
    const data = error.response?.data;
    const payload = coercePayload(data);
    const isTimeout = error.code === 'ECONNABORTED';
    const isNetworkError = !error.response && !isTimeout;

    const code = isTimeout
        ? 'TIMEOUT'
        : isNetworkError
          ? 'NETWORK_ERROR'
          : codeFromStatus(status, payload.code as ApiErrorCode | undefined);

    const message = isTimeout
        ? 'Request timed out.'
        : isNetworkError
          ? 'Network request failed.'
          : extractMessage(data, error.message || fallbackMessage);

    return new ApiServiceError(message, {
        code,
        data,
        details: payload.errors,
        isRetryable: isTimeout || isNetworkError || isRetryableStatus(status),
        requestId: requestIdFrom(error),
        status,
    });
}

export function getApiErrorMessage(error: unknown, fallbackMessage = 'Something went wrong'): string {
    return toApiServiceError(error, fallbackMessage).message;
}
